import React, { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserProps from '../../proptypes/user';
import style from '../../style/components/profile/WalletPersonalProfile.module.scss';
import ChangeRequestsModal from '../changeRequests/ChangeRequestsModal';
import UpdateAddressModal from '../changeRequests/UpdateAddressModal';
import SuccessModal from '../SuccessModal';
import WalletSectionCard from './WalletSectionCard';
import {
  addressLine1,
  addressLine2,
  isEmptyAddress,
} from '../../utils/address';
import { formatPhone } from '../../utils/phone';

const WalletPersonalProfile = ({ user }) => {
  const { t } = useTranslation();
  const noName = !user.fname && !user.lname;
  const noAddress = isEmptyAddress(user);
  const noPhone = !user.phone;

  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  return (
    <WalletSectionCard
      title={t('profile.personalProfile')}
      icon={<idme-icon name="user" color="stone" size="large" />}
    >
      {noName && user.can_request_name_change ? null : (
        <>
          <section className={style.field}>
            <div>
              <h4 className={style.label}>{t('profile.name')}</h4>
              <p className={style.value} data-testid="profile-name">
                {noName ? t('profile.noName') : `${user.fname} ${user.lname}`}
              </p>
            </div>
            {user.can_request_name_change && (
              <button
                type="button"
                className={style.actionLink}
                onClick={() => setIsNameModalOpen(true)}
              >
                {t('profile.update')}
              </button>
            )}
          </section>
          <div className={style.separator} />
        </>
      )}
      <section className={style.field}>
        <div>
          <h4 className={style.label}>{t('profile.address')}</h4>
          <p className={style.value} data-testid="profile-address">
            {noAddress ? t('profile.noAddress') : addressLine1(user)}
          </p>
          {!noAddress && <p className={style.value}>{addressLine2(user)}</p>}
        </div>
        <button
          type="button"
          className={style.actionLink}
          onClick={() => setIsAddressModalOpen(true)}
        >
          {t('profile.edit')}
        </button>
      </section>
      <div className={style.separator} />
      <section className={style.field}>
        <div>
          <h4 className={style.label}>{t('profile.phone')}</h4>
          <p className={style.value} data-testid="profile-phone">
            {noPhone ? t('profile.noPhone') : formatPhone(user.phone, false)}
          </p>
        </div>
      </section>

      <ChangeRequestsModal
        isOpen={isNameModalOpen}
        closeModal={() => setIsNameModalOpen(false)}
      />
      {isAddressModalOpen && (
        <UpdateAddressModal
          address={user}
          isOpen={isAddressModalOpen}
          closeModal={() => setIsAddressModalOpen(false)}
          openSuccessModal={() => setIsSuccessModalOpen(true)}
        />
      )}
      {isSuccessModalOpen && (
        <SuccessModal
          text={t('profile.addressUpdated')}
          isOpen={isSuccessModalOpen}
          closeModal={() => setIsSuccessModalOpen(false)}
        />
      )}
    </WalletSectionCard>
  );
};

WalletPersonalProfile.propTypes = {
  user: UserProps,
};

export default memo(WalletPersonalProfile);
